import { type FormEvent, useEffect, useState } from "react";
import { formatDate } from "@/lib/date";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, ApiError } from "@/api/client";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { type Project } from "@/hooks/useProjects";
import { useProjectsPaginated } from "@/hooks/useProjectsPaginated";
import { usePageState } from "@/hooks/usePageState";
import { mapApiError } from "@/lib/errors";
import { queryKeys } from "@/lib/queryKeys";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/ui/page-header";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CalendarDays, FileText, FolderOpen, Grid3x3, Hash, LayoutList, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { EmptyState } from "@/components/ui/empty-state";
import { TableSkeleton } from "@/components/ui/table-skeleton";
import { TablePagination } from "@/components/ui/table-pagination";
import { ProjectCard } from "@/components/project/ProjectCard";
import { useProjectsStats } from "@/hooks/useProjectsStats";

const SLUG_PATTERN = /^[a-z0-9-]+$/;

export function ProjectsPage() {
  const isAdmin = useIsAdmin();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const { page, pageSize, totalPages, setPage, handlePageSizeChange } = usePageState(10);
  const { projects, total, loading, error, refetch } = useProjectsPaginated(page, pageSize);
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [createOpen, setCreateOpen] = useState(false);
  const [deletePending, setDeletePending] = useState<Project | null>(null);
  const [editPending, setEditPending] = useState<Project | null>(null);
  const projectSlugs = projects.map((p) => p.slug);
  const statsMap = useProjectsStats(projectSlugs);
  const sortedProjects = [...projects].sort((a, b) => {
    const aActive = statsMap[a.slug]?.lastActive ?? a.created_at;
    const bActive = statsMap[b.slug]?.lastActive ?? b.created_at;
    return new Date(bActive).getTime() - new Date(aActive).getTime();
  });

  // D6: cache invalidation replaces the hand-rolled projectsBus event —
  // invalidates both the sidebar's useProjects() list and this page's own
  // paginated cache in one call (both share the `projects.all` prefix).
  function invalidateProjects() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
  }

  function handleCreated(project: Project) {
    setCreateOpen(false);
    refetch();
    invalidateProjects();
    navigate(`/${orgSlug}/projects/${project.slug}`);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={FolderOpen}
        title={t("projects.header.title")}
        subtitle={isAdmin ? t("projects.header.subtitleAdmin") : t("projects.header.subtitleMember")}
        stats={!loading && total > 0 ? [{ value: total, label: t("projects.stats.total") }] : undefined}
        action={
          isAdmin && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-1.5 shadow-sm">
                  <Plus className="h-4 w-4" />
                  {t("projects.createButton")}
                </Button>
              </DialogTrigger>
              <CreateProjectDialog onSuccess={handleCreated} />
            </Dialog>
          )
        }
      />

      {/* View toggle */}
      {!loading && total > 0 && (
        <div className="flex justify-end gap-1">
          <Button
            variant={viewMode === "grid" ? "default" : "outline"}
            size="icon"
            onClick={() => setViewMode("grid")}
            aria-label={t("projects.view.grid", "Grid view")}
            aria-pressed={viewMode === "grid"}
          >
            <Grid3x3 className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === "table" ? "default" : "outline"}
            size="icon"
            onClick={() => setViewMode("table")}
            aria-label={t("projects.view.table", "Table view")}
            aria-pressed={viewMode === "table"}
          >
            <LayoutList className="h-4 w-4" />
          </Button>
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {!loading && total === 0 ? (
        <EmptyState
          icon={FolderOpen}
          title={t("projects.empty.title")}
          description={t("projects.empty.description")}
          action={isAdmin ? { label: t("projects.createButton"), onClick: () => setCreateOpen(true) } : undefined}
        />
      ) : viewMode === "grid" ? (
        <>
          {loading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="h-44 animate-pulse rounded-lg border bg-muted/50" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {sortedProjects.map((p) => (
                <ProjectCard
                  key={p.id}
                  project={p}
                  onClick={() => navigate(`/${orgSlug}/projects/${p.slug}`)}
                  stats={statsMap[p.slug]}
                />
              ))}
            </div>
          )}
          <TablePagination
            currentPage={page}
            totalPages={totalPages(total)}
            onPrev={() => setPage((pg) => pg - 1)}
            onNext={() => setPage((pg) => pg + 1)}
            hasNext={page < totalPages(total)}
            hasPrev={page > 1}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[220px]">
                  <span className="flex items-center gap-1.5"><FolderOpen className="h-3.5 w-3.5" />{t("projects.table.name")}</span>
                </TableHead>
                <TableHead>
                  <span className="flex items-center gap-1.5"><Hash className="h-3.5 w-3.5" />{t("projects.table.slug")}</span>
                </TableHead>
                <TableHead>
                  <span className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" />{t("projects.table.description")}</span>
                </TableHead>
                <TableHead>
                  <span className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{t("projects.table.created")}</span>
                </TableHead>
                {isAdmin && <TableHead className="w-[100px]" />}
              </TableRow>
            </TableHeader>
            {loading ? (
              <TableSkeleton columns={4} />
            ) : (
              <TableBody>
                {sortedProjects.map((p) => (
                  <TableRow
                    key={p.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/${orgSlug}/projects/${p.slug}`)}
                  >
                    <TableCell className="whitespace-nowrap">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 ring-1 ring-primary/20">
                          <FolderOpen className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <span className="font-medium">{p.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs whitespace-nowrap">{p.slug}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {p.description || "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {formatDate(p.created_at)}
                    </TableCell>
                    {isAdmin && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={t("projects.actions.edit")}
                            onClick={() => setEditPending(p)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={t("projects.actions.delete")}
                            onClick={() => setDeletePending(p)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
          <TablePagination
            currentPage={page}
            totalPages={totalPages(total)}
            onPrev={() => setPage((p) => p - 1)}
            onNext={() => setPage((p) => p + 1)}
            hasNext={page < totalPages(total)}
            hasPrev={page > 1}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}

      <DeleteProjectDialog
        project={deletePending}
        onClose={() => setDeletePending(null)}
        onSuccess={() => { setDeletePending(null); refetch(); invalidateProjects(); }}
      />
      <EditProjectDialog
        project={editPending}
        onClose={() => setEditPending(null)}
        onSuccess={() => { setEditPending(null); refetch(); invalidateProjects(); }}
      />
    </div>
  );
}

function DeleteProjectDialog({
  project,
  onClose,
  onSuccess,
}: {
  project: Project | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);

  async function handleConfirm() {
    if (!project) return;
    setSubmitting(true);
    try {
      await api(`/api/v2/projects/${project.id}`, { method: "DELETE" });
      toast.success(t("projects.toast.deleteSuccess"));
      onSuccess();
    } catch {
      toast.error(t("projects.toast.deleteError"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AlertDialog open={!!project} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("projects.dialogs.delete.title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("projects.dialogs.delete.descriptionPrefix")}
            <strong>{project?.name}</strong>
            {t("projects.dialogs.delete.descriptionSuffix")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>{t("projects.dialogs.delete.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={submitting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" />{t("projects.dialogs.delete.confirming")}</> : t("projects.dialogs.delete.confirm")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EditProjectDialog({
  project,
  onClose,
  onSuccess,
}: {
  project: Project | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (project) {
      setName(project.name);
      setDescription(project.description ?? "");
      setError("");
    }
  }, [project]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!project) return;
    setSubmitting(true);
    setError("");
    try {
      await api(`/api/v2/projects/${project.id}`, {
        method: "PATCH",
        body: JSON.stringify({ name, description: description || undefined }),
      });
      toast.success(t("projects.toast.updateSuccess"));
      onSuccess();
    } catch {
      setError(t("projects.toast.updateError"));
      toast.error(t("projects.toast.updateError"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={!!project} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("projects.dialogs.edit.title")}</DialogTitle>
          <DialogDescription>{t("projects.dialogs.edit.description")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-project-name">{t("projects.dialogs.edit.nameLabel")}</Label>
            <Input
              id="edit-project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={submitting}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-project-desc">
              {t("projects.dialogs.edit.descriptionLabel")}{" "}
              <span className="text-muted-foreground text-xs">{t("projects.dialogs.edit.descriptionOptional")}</span>
            </Label>
            <Input
              id="edit-project-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={submitting}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              {t("projects.dialogs.edit.cancel")}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" />{t("projects.dialogs.edit.submitting")}</> : t("projects.dialogs.edit.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateProjectDialog({ onSuccess }: { onSuccess: (project: Project) => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const slugInvalid = slug.length > 0 && !SLUG_PATTERN.test(slug);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const project = await api<Project>("/api/v2/projects", {
        method: "POST",
        body: JSON.stringify({
          name,
          slug,
          description: description || undefined,
        }),
      });
      setName("");
      setSlug("");
      setDescription("");
      onSuccess(project);
    } catch (err) {
      const msg = mapApiError(
        err,
        {
          409: t("projects.toast.createConflict"),
          422: t("projects.toast.slugInvalidFormat"),
        },
        t("projects.toast.createError"),
      );
      setError(msg);
      if (err instanceof ApiError && err.status === 422) toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t("projects.dialogs.create.title")}</DialogTitle>
        <DialogDescription>{t("projects.dialogs.create.description")}</DialogDescription>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="project-name">{t("projects.dialogs.create.nameLabel")}</Label>
          <Input
            id="project-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Payment Gateway"
            required
            disabled={submitting}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="project-slug">{t("projects.dialogs.create.slugLabel")}</Label>
          <Input
            id="project-slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="payment-gw"
            pattern={SLUG_PATTERN.source}
            required
            disabled={submitting}
          />
          {slugInvalid && (
            <p className="text-sm text-destructive">{t("projects.toast.slugInvalidFormat")}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="project-desc">{t("projects.dialogs.create.descriptionLabel")} <span className="text-muted-foreground text-xs">{t("projects.dialogs.create.descriptionOptional")}</span></Label>
          <Input
            id="project-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Core payment processing"
            disabled={submitting}
          />
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button type="submit" disabled={submitting || slugInvalid}>
            {submitting ? (
              <>
                <Loader2 className="animate-spin" />
                {t("projects.dialogs.create.submitting")}
              </>
            ) : (
              t("projects.dialogs.create.submit")
            )}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
